import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubjectLanguageVariants } from '@features/user';
import { Alert, Button, Skeleton, Stack } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { localTodayCalendarDate } from '@shared/dates/calendarDate';
import { useSnackbar } from 'notistack';

import type { ClaimWorkCounts, ClaimWorkViewKey } from '../../../store';
import { useClaimWorkQuery } from '../../../store';
import type { ClaimWorkPageResponse, QueueViewKey } from '../../../types/encounterWork';
import { QUEUE_RUN_PAGE_LIMIT, type QueueRun, queueRunEntries } from '../../../utils/queueRun';
import {
  workbenchProvenanceState,
  type WorkbenchQueueEntry,
} from '../../../utils/worklistProvenance';
import { selectNewerClaimWorkCounts } from '../claims/claimWorkCounts';
import { IssueDetailContainer, ReasonDetailContainer } from '../work';

import { HomeEmptyState } from './HomeEmptyState';
import {
  HOME_QUEUE_ROW_LIMIT,
  HOME_QUEUES,
  type HomeQueueDefinition,
  type HomeQueueRow,
} from './homeQueues';
import { HomeQueuePanel, HomeQueueStrip } from './HomeQueueStrip';
import { buildHomeQueueStrip, type HomeQueueRead } from './homeQueueTiles';
import { applyHomeUrlState, homeReturnUrl, parseHomeUrlState } from './homeUrlState';
import { QueueCard } from './QueueCard';
import { useHomeArrivalPrefetch, useHomeClaimPrefetch } from './useHomeClaimPrefetch';
import { useHomeQueueRuns } from './useHomeQueueRuns';

/**
 * The counts payload once ADR-0153 lands, read defensively until it has.
 *
 * `hidden_count` and the eight `work_view_counts` keys ride on the PAGE
 * response (`ClaimWorkPageResponse`), so the five reads Home already makes
 * carry them; there is no separate counts call to add. Both stay optional
 * here, and the keys inside `work_view_counts` stay `Partial` whatever the
 * contract promises, because the deployed server has not shipped them yet
 * and an absent key must stay absent rather than read as zero. This is the
 * boundary where the generated client's index signature meets the shared
 * type's required keys; packet F (GDN-3382) regenerates the client and
 * collapses the two.
 */
type HomeCounts = ClaimWorkCounts & {
  hidden_count?: ClaimWorkPageResponse['counts']['hidden_count'];
  work_view_counts?: Partial<ClaimWorkPageResponse['counts']['work_view_counts']>;
};

/**
 * JotBill Home: what needs the biller right now (ADR-010).
 *
 * The container fetches and dispatches; `HomeQueueStrip`, `QueueCard` and
 * `HomeEmptyState` render. The strip (GDN-4085) is five tiles in priority
 * order, one per queue, and the selected queue's table below them. The
 * container owns the selection and keeps it in the URL (`homeUrlState`);
 * what each tile says, and which queue is selected when the URL names none,
 * is `buildHomeQueueStrip`'s call, made here from the reads.
 *
 * Five page reads, one per queue, five oldest rows each — no new
 * RPC, and no sixth call for the counts: `get_claim_work_page` already
 * returns `work_view_counts` for every key, so the newest of the five
 * snapshots IS the counts read, chosen by the same rule the Claims rail uses
 * when its per-view caches disagree.
 *
 * `include_hidden` is not sent. The parameter defaults to false server-side
 * (contract section 4) and the generated client does not carry it yet;
 * packet F regenerates the client and this becomes explicit.
 *
 * Behind the cards, the WHOLE queue: "Work this queue" and a card row both
 * enter the run behind the card (`utils/queueRun.ts`), because Home is an
 * entry into the queue, not into the five rows it happened to show. The
 * runs are read ahead while Home is open (`useHomeQueueRuns`), and a click
 * leaves on the run's first page; the rest keeps loading behind the biller,
 * and the workbench's rail grows as it lands.
 *
 * The CLAIM behind the click is warmed too (`useHomeClaimPrefetch`): the run
 * says which claims come next, but the workbench still has to read the one
 * being opened, and that read — the claim-workspace GET at a ~2.35s median
 * (GDN-3199) — is what the biller waits on. The Claims worklist has warmed it
 * on row hover since GDN-3201; Home warms it from all three of its own
 * moments: a row under the pointer, "Work this queue" under the pointer, and
 * arrival, which warms each queue's first step before there is a pointer at
 * all.
 */

/** One queue's page. Oldest first, because the oldest is the one at risk. */
const useQueuePage = (queue: QueueViewKey) =>
  useClaimWorkQuery(
    {
      limit: HOME_QUEUE_ROW_LIMIT,
      sort: 'service_date_asc',
      /* The five queue keys are the contract's (`bill_secondary` since
         GDN-4023); the generated `ClaimWorkViewKey` still names only the
         views it knew before the queues. Packet F removes this cast. */
      workView: queue as unknown as ClaimWorkViewKey,
    },
    { refetchOnFocus: true, refetchOnMountOrArgChange: 15, refetchOnReconnect: true },
  );

/** What the click hands the workbench: a live run to read, or the rows it saw. */
interface HandedRun {
  runId: string | null;
  entries: WorkbenchQueueEntry[];
  viewCount: number | null;
}

export const HomeContainer: React.FC = () => {
  const navigate = useNavigate();
  const { singularCap } = useSubjectLanguageVariants();
  const { billing } = useEventTracking();
  /* The selected queue lives in the URL, so Back from the workbench lands on
     the same tile. Written by REPLACE, like the Claims worklist's state: a
     tile is a view of Home, not a place in the history. */
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKey = useMemo(() => parseHomeUrlState(searchParams).view, [searchParams]);

  const fix = useQueuePage('fix');
  const review = useQueuePage('review');
  const billSecondary = useQueuePage('bill_secondary');
  const submit = useQueuePage('submit');
  const awaitingPayer = useQueuePage('awaiting_payer');

  /* Index-aligned with HOME_QUEUES, which is why the five calls above are
     spelled out in that order rather than mapped: hooks cannot be called in
     a loop, and a lookup keyed by queue would hide the alignment. The order
     is the priority order (GDN-4068); reorder both together or neither. */
  const results = useMemo(
    () => [fix, review, billSecondary, submit, awaitingPayer],
    [awaitingPayer, billSecondary, fix, review, submit],
  );

  useEffect(() => {
    billing.homeOpened();
    /* Once per arrival. `billing` is a fresh object every render. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* The newest canonical snapshot across the five per-view caches. */
  const counts = useMemo(
    () =>
      results.reduce<HomeCounts | undefined>(
        (newest, result) => selectNewerClaimWorkCounts(newest, result.data?.counts),
        undefined,
      ),
    [results],
  );
  /* Keys the server did not send stay ABSENT here rather than becoming zero.
     `work_view_counts` is optional on the wire and is keyed by the new queue
     keys, so an older server answers rows with no counts for them, and a card
     reading 0 above five visible rows would be a lie the footer then hides. */
  const viewCounts = useMemo<Record<string, number | undefined>>(
    () => counts?.work_view_counts ?? {},
    [counts?.work_view_counts],
  );

  const rowsByQueue = useMemo(
    () => results.map(result => (result.data?.items ?? []) as HomeQueueRow[]),
    [results],
  );

  /* The default, once the counts have chosen it, holds for this visit. A
     newer snapshot (a read landing after the others, a refetch on focus) can
     change a count or clear a tile, but never swap the table under the
     biller. A choice in the URL always wins. */
  const [settledDefault, setSettledDefault] = useState<QueueViewKey | null>(null);

  /* Each tile's state and the selected queue. The counts settle every tile
     from the first read that answers, so the strip does not reshuffle as the
     later reads fill in their tables. */
  const strip = useMemo(() => {
    const reads: HomeQueueRead[] = HOME_QUEUES.map((queue, index) => ({
      count: viewCounts[queue.key],
      failed: Boolean(results[index].error),
      key: queue.key,
      loading: results[index].isLoading,
      rows: rowsByQueue[index],
    }));
    return buildHomeQueueStrip(reads, requestedKey ?? settledDefault, localTodayCalendarDate());
  }, [requestedKey, results, rowsByQueue, settledDefault, viewCounts]);

  useEffect(() => {
    if (!requestedKey && !settledDefault && strip.selectedKey) {
      setSettledDefault(strip.selectedKey);
    }
  }, [requestedKey, settledDefault, strip.selectedKey]);

  const selectQueue = useCallback(
    (key: QueueViewKey) => {
      if (key === strip.selectedKey) return;
      billing.homeTileSelected({
        clear: strip.tiles.some(tile => tile.queue.key === key && tile.clear),
        queue: key,
      });
      setSearchParams(prev => applyHomeUrlState(prev, { view: key }), { replace: true });
    },
    [billing, setSearchParams, strip],
  );

  /* THE RUN'S first step per queue, which is not always the card's first row.
     `queueRunEntries` excludes rows the workbench cannot hold (a null
     `encounter_id`, contract v1.2), so if the oldest row is one of those,
     starting at `rows[0]` would open a claim that is not IN the queue it just
     claimed to start: the gate renders the legacy view, `deriveQueueContext`
     finds no position for it, and the biller lands with no rail and no Next.
     Start where the run starts — and warm the same row, for the same reason:
     it is the one "Work this queue" opens. */
  const firstInRunByQueue = useMemo(
    () => rowsByQueue.map(rows => rows.find(row => row.encounter_id)),
    [rowsByQueue],
  );

  const prefetch = useHomeClaimPrefetch();
  /* The five first steps, in card order: the arrival warm's whole appetite. */
  const arrivalTargets = useMemo(
    () => firstInRunByQueue.filter((row): row is HomeQueueRow => Boolean(row)),
    [firstInRunByQueue],
  );
  useHomeArrivalPrefetch(arrivalTargets, prefetch.warm);

  const runs = useHomeQueueRuns();
  const { enqueueSnackbar } = useSnackbar();
  /* The row to open once its run's first page is in. ONE target across the
     five cards, and the LAST click is it: a biller who clicks a second row
     while a page loads goes where they last pointed, whichever read
     finishes first, and one navigation happens in all. */
  const latestClick = useRef<{
    queue: QueueViewKey;
    item: HomeQueueRow;
    rows: HomeQueueRow[];
  } | null>(null);
  /* The queue whose first page a click is waiting on; its button says so. */
  const [awaiting, setAwaiting] = useState<QueueViewKey | null>(null);
  const mounted = useRef(false);
  useEffect(() => {
    /* Set on every mount, not at the first render: StrictMode rehearses the
       effect (mount, unmount, mount), and a flag that only ever went false
       would dead-end every click after the rehearsal. */
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const openInWorkbench = useCallback(
    (queue: HomeQueueDefinition, item: HomeQueueRow, handed: HandedRun) => {
      navigate(
        `/jotbill/encounter/${item.encounter_id}?workspace=claim&claim_id=${item.claim_id}`,
        {
          state: workbenchProvenanceState({
            /* Home, on the tile this claim was opened from. */
            url: homeReturnUrl(searchParams, queue.key),
            label: 'Home',
            queue:
              handed.entries.length > 0
                ? {
                    done: false,
                    entries: handed.entries,
                    handled: 0,
                    /* The product's own name for this queue. */
                    label: queue.label,
                    /* The run the workbench reads the rest of the queue from
                     as it lands; `entries` is only the fallback for a
                     reload that finds the cache gone. */
                    runId: handed.runId,
                    savedViewId: null,
                    /* The server's count, so the rail can admit when it holds
                     fewer rows than the queue does. */
                    viewCount: handed.viewCount,
                    viewKey: queue.key,
                  }
                : null,
          }),
        },
      );
    },
    [navigate, searchParams],
  );

  /* The first page is in, or has failed: whichever row was clicked last
     goes to the workbench, inside the run when the run holds it. */
  const finishRun = useCallback(
    (queue: HomeQueueDefinition, run: QueueRun | null, runId: string) => {
      const target = latestClick.current;
      /* Nothing to open, Home already left, or the biller has since clicked
         into another queue, whose own read will do the opening. */
      if (!target || target.queue !== queue.key || !mounted.current) return;
      latestClick.current = null;
      setAwaiting(null);
      /* The biller clicked THIS row, so the run they get has to contain it.
         When the read failed, or the queue moved under the click and the row
         is no longer in it, the rows they saw are the run — and there is no
         live run for the rail to read past them. */
      const inRun = run?.entries.some(entry => entry.claimId === target.item.claim_id) ?? false;
      openInWorkbench(
        queue,
        target.item,
        inRun && run
          ? {
              /* At most one page in history state; the rest lives in the cache. */
              entries: run.entries.slice(0, QUEUE_RUN_PAGE_LIMIT),
              runId,
              viewCount: run.viewCount,
            }
          : {
              entries: queueRunEntries(target.rows),
              runId: null,
              viewCount: run?.viewCount ?? viewCounts[queue.key] ?? null,
            },
      );
    },
    [openInWorkbench, viewCounts],
  );

  const openRow = useCallback(
    (queue: HomeQueueDefinition, rows: HomeQueueRow[], item: HomeQueueRow) => {
      billing.homeQueueSelected({
        /* GDN-3201's meter, on Home's own surface: whether the claim was
           already warm when the click landed — by hover, by the button, or
           by the arrival warm. Read before anything else, because the click
           itself warms nothing. */
        prefetched: prefetch.wasPrefetched(item.claim_id),
        queue: queue.key,
      });
      /* `encounter_id` is the claim's FIRST line's encounter and is NULLABLE
         (contract v1.1). Such a claim routes through the /jotbill/claim/:id
         gate to the legacy view, which has no rail and no Next: it is not a
         step in any run, so there is no queue to read for it. */
      if (!item.encounter_id) {
        navigate(`/jotbill/claim/${item.claim_id}`, {
          state: workbenchProvenanceState({
            url: homeReturnUrl(searchParams, queue.key),
            label: 'Home',
          }),
        });
        return;
      }
      latestClick.current = { item, queue: queue.key, rows };
      const { firstPage, loaded, runId } = runs.open(queue.key);
      /* The first page is already in hand: go now. */
      if (loaded) {
        finishRun(queue, loaded, runId);
        return;
      }
      setAwaiting(queue.key);
      void firstPage.then(
        run => finishRun(queue, run, runId),
        () => {
          /* A read cut short by leaving Home is not an error to report, and
             a click the biller has since moved on from is not one either.
             Two clicks share one promise; the first to land clears the
             target, so the failure is said once. */
          if (!mounted.current || latestClick.current?.queue !== queue.key) return;
          enqueueSnackbar('Could not load the whole queue. Starting with the rows shown.', {
            variant: 'error',
          });
          finishRun(queue, null, runId);
        },
      );
    },
    [billing, enqueueSnackbar, finishRun, navigate, prefetch, runs, searchParams],
  );

  /* The Issues popover on a Claims to review row (GDN-3379). The container
     inside reads the claim's certification, and only once the popover is
     open: Home does not warm that cache on row hover the way the Claims tab
     does, so the first open shows the row and appends the rest. */
  const renderIssueDetail = useCallback(
    (item: HomeQueueRow, headline: string) => (
      <IssueDetailContainer headline={headline} item={item} />
    ),
    [],
  );
  /* One event per open; the surface and the cell kind are closed enums. */
  const onIssueDetailOpen = useCallback(
    () => billing.issueDetailOpened({ kind: 'issues', surface: 'home' }),
    [billing],
  );

  const renderReasonDetail = useCallback(
    (item: HomeQueueRow) => <ReasonDetailContainer item={item} />,
    [],
  );
  const onReasonDetailOpen = useCallback(
    () => billing.issueDetailOpened({ kind: 'reason', surface: 'home' }),
    [billing],
  );

  const anyLoading = results.some(result => result.isLoading);
  const allFailed = results.every(result => Boolean(result.error));
  const anyFailed = results.some(result => Boolean(result.error));

  if (allFailed) {
    return (
      <Alert
        action={
          <Button
            color="inherit"
            onClick={() => {
              results.forEach(result => void result.refetch());
            }}
            size="small"
          >
            Try again
          </Button>
        }
        severity="error"
      >
        Claim work could not be loaded.
      </Alert>
    );
  }

  /* Zero everywhere, and every read answered: the sunshine is a FACT about
     the organization's work, so it must never stand in for a failed read —
     nor for an ABSENT one. Both the counts and the rows have to agree it is
     empty, because a server that does not know these queue keys yet answers
     no counts at all, and counts alone would read that silence as a clear
     desk. */
  const everyQueueEmpty =
    !anyLoading &&
    !anyFailed &&
    rowsByQueue.every(rows => rows.length === 0) &&
    /* `?? 0` is safe HERE and only here, because the line above already
       required the rows to be empty too: no rows and no count is an empty
       queue, whereas no count on its own is merely an unanswered question. */
    HOME_QUEUES.every(queue => (viewCounts[queue.key] ?? 0) === 0);

  if (everyQueueEmpty) {
    return <HomeEmptyState hiddenCount={counts?.hidden_count ?? 0} />;
  }

  /* What the panel under the strip shows: the selected queue's table, its
     all-clear line, or a placeholder while its read is still out. The
     counts can say a queue is empty before its own read answers, so an
     empty queue never waits. */
  const renderPanel = () => {
    const index = HOME_QUEUES.findIndex(queue => queue.key === strip.selectedKey);
    if (index < 0) {
      return <Skeleton data-testid="home-queue-panel-skeleton" height={340} variant="rounded" />;
    }
    const queue = HOME_QUEUES[index];
    const tile = strip.tiles[index];
    const result = results[index];
    if (!tile.clear && result.isLoading) {
      return (
        <Skeleton data-testid={`home-queue-skeleton-${queue.key}`} height={340} variant="rounded" />
      );
    }
    const rows = rowsByQueue[index];
    const firstInRun = firstInRunByQueue[index];
    return (
      <QueueCard
        key={queue.key}
        canWorkQueue={Boolean(firstInRun)}
        clear={tile.clear}
        count={viewCounts[queue.key]}
        error={Boolean(result.error)}
        pending={awaiting === queue.key}
        queue={queue}
        renderIssueDetail={renderIssueDetail}
        renderReasonDetail={renderReasonDetail}
        onReasonDetailOpen={onReasonDetailOpen}
        rows={rows}
        subjectLabel={singularCap}
        onIssueDetailOpen={onIssueDetailOpen}
        onOpenRow={item => openRow(queue, rows, item)}
        onRetry={() => void result.refetch()}
        onRowHover={prefetch.prefetchOnHover}
        onWorkQueue={() => {
          if (firstInRun) openRow(queue, rows, firstInRun);
        }}
        onWorkQueueHover={() => {
          if (firstInRun) prefetch.prefetchOnHover(firstInRun);
        }}
      />
    );
  };

  return (
    <Stack spacing={2.5}>
      <HomeQueueStrip tiles={strip.tiles} onSelect={selectQueue} />
      <HomeQueuePanel selectedKey={strip.selectedKey}>{renderPanel()}</HomeQueuePanel>
    </Stack>
  );
};

export default HomeContainer;
